import IdeateGraph from '../graphs/IdeateGraph';
import { IDEATE_RECOMMENDATIONS } from '../constants';
import { ANALYSES_TYPES, AnalysisKey } from '../../../../src/commonConstants';
import { ClusterAnalysis } from '../../../../src/commonTypes';
import { ideateRecommenderDismissed } from '../tutorials/tutorialEvents';

const analyseForm = document.querySelector<HTMLElement>('#analyse-form-container');
const insights = document.querySelectorAll<HTMLInputElement>('#analysesForm .first-question input');
const recommendedAnalysesContainer = document.querySelector<HTMLFieldSetElement>(
	'.recommended-analyses'
);
const analyseContainer = document.querySelector<HTMLFieldSetElement>('.analyse-options');
const ANALYSIS_DESCRIPTIONS: Partial<Record<AnalysisKey, string>> = {
	'Psych.cluster':
		'Maps the nodes on your Inspire board to personality-trait groups using the Personality analysis taxonomy.',
	'Archetype.cluster':
		'Maps the nodes on your Inspire board to recognisable character and brand archetype groups.',
	'Plots.cluster':
		'Maps the nodes on your Inspire board to narrative plot patterns for shaping stories and characters.',
	'Themes.cluster':
		'Derives recurring themes from the nodes on your Inspire board and groups the matching ideas beneath them.',
	'Random0.cluster':
		'Regroups the nodes on your Inspire board through less-obvious associations to open up new directions.',
	'Greek.cluster':
		'Maps the nodes on your Inspire board to Greek-god archetypes as a creative frame for roles and motivations.',
	'Motivations.cluster':
		'Maps the nodes on your Inspire board to motivation groups representing needs, drivers and desired outcomes.',
};

const getInsightName = (insight: HTMLInputElement): string => {
	const label = insight.labels?.[0];
	return label?.dataset.insight || label?.innerText.trim() || '';
};

const canConfigurePositioning = (): boolean =>
	document.body.dataset.boardOwner === 'true' &&
	(globalThis.neuroCreate.graph as IdeateGraph | undefined)?.canConfigurePositioning() !== false;

const updateSelectedAnalysisCount = () => {
	if (!analyseForm) return;
	const selectedCount = analyseForm.querySelectorAll<HTMLInputElement>(
		'.form-recommend fieldset input:checked'
	).length;
	const count = analyseForm.querySelector<HTMLElement>('.selected-analysis-count');
	const submit = analyseForm.querySelector<HTMLButtonElement>('#board-form-submit');
	if (count) count.innerText = String(selectedCount);
	if (submit) submit.disabled = selectedCount === 0;
};

const createAnalysisOption = (analysisKey: AnalysisKey, analysis: ClusterAnalysis) => {
	const input = document.createElement('input');
	input.id = `ideate-analysis-${analysisKey}`;
	input.type = 'checkbox';
	input.value = analysisKey;

	const label = document.createElement('label');
	label.htmlFor = input.id;
	label.title = analysis.category
		? `${analysis.name} — ${analysis.category}`
		: `${analysis.name} psychographic analysis`;

	const name = document.createElement('span');
	name.className = 'analysis-option-name';
	name.innerText = analysis.name;

	const state = document.createElement('span');
	state.className = 'analysis-option-state';
	state.setAttribute('aria-hidden', 'true');
	state.innerText = '✓';

	const description = document.createElement('span');
	description.className = 'analysis-option-description';
	description.innerText =
		ANALYSIS_DESCRIPTIONS[analysisKey] ||
		'Finds a distinct psychographic pattern in the ideas and context on your board.';
	label.append(name, description, state);
	input.addEventListener('change', updateSelectedAnalysisCount);
	return { input, label };
};

const renderRecommendationsForInsight = (insightName: string): boolean => {
	if (!recommendedAnalysesContainer || !analyseContainer) return false;
	const answer = IDEATE_RECOMMENDATIONS.find((item) => item.name === insightName);
	if (!answer) return false;

	recommendedAnalysesContainer.innerHTML = '';
	analyseContainer.innerHTML = '';
	document.querySelector<HTMLElement>('#analysesForm .analyses-name')!.innerText = insightName;

	answer.recommendations.forEach((analysisKey) => {
		if (analysisKey === 'colors.cluster') return;
		const option = createAnalysisOption(analysisKey, ANALYSES_TYPES[analysisKey]);
		option.input.checked = true;
		recommendedAnalysesContainer.append(option.input, option.label);
	});

	const recommendedAnalyses = answer.recommendations;
	const remainingAnalyses = (
		Object.entries(ANALYSES_TYPES) as Array<[AnalysisKey, ClusterAnalysis]>
	).filter(
		([analysisKey]) =>
			analysisKey !== 'colors.cluster' &&
			analysisKey !== 'Grounding.cluster' &&
			!recommendedAnalyses.includes(analysisKey)
	);

	remainingAnalyses.forEach(([analysisKey, analysis]) => {
		if (analysis.category === 'Motivations') return;
		const option = createAnalysisOption(analysisKey, analysis);
		analyseContainer.append(option.input, option.label);
	});

	updateSelectedAnalysisCount();
	return true;
};

export const selectIdeateInsight = (insightName: string): boolean => {
	if (!canConfigurePositioning()) return false;
	const normalizedInsight = insightName.trim();
	const input = Array.from(insights).find(
		(candidate) => getInsightName(candidate) === normalizedInsight
	);
	if (!input) return false;
	input.checked = true;
	input.dispatchEvent(new Event('click', { bubbles: true }));
	return true;
};

(globalThis as unknown as { flowySelectIdeateInsight?: (insightName: string) => boolean })
	.flowySelectIdeateInsight = selectIdeateInsight;

if (analyseForm) {
	if (document.body.dataset.boardOwner !== 'true') {
		analyseForm.classList.add('hidden');
		analyseForm.setAttribute('aria-hidden', 'true');
		insights.forEach((insight) => {
			insight.disabled = true;
		});
	}

	const dismissRecommender = (reason: 'closed' | 'started') => {
		analyseForm.classList.add('hidden');
		(globalThis.neuroCreate.graph as IdeateGraph | undefined)?.acknowledgeCurrentWelcome();
		const selectedInsight = Array.from(insights).find((input) => input.checked);
		const selectedAnalyses = Array.from(
			analyseForm.querySelectorAll<HTMLInputElement>('.form-recommend fieldset input:checked')
		).map((input) => input.value);
		ideateRecommenderDismissed(reason, {
			insight: selectedInsight ? getInsightName(selectedInsight) : null,
			analyses: selectedAnalyses,
		});
	};

	analyseForm.querySelector('.close-button')?.addEventListener('click', () => {
		dismissRecommender('closed');
	});
	analyseForm.querySelector('#board-form-submit')?.addEventListener('click', (event: Event) => {
		event.preventDefault();
		if (!canConfigurePositioning()) {
			analyseForm.classList.add('hidden');
			return;
		}
		const analyseAnswers = Array.from(
			analyseForm.querySelectorAll<HTMLInputElement>('.form-recommend fieldset input:checked')
		).map((input) => input.value) as Array<AnalysisKey>;
		const positioningInsight = Array.from(insights).find((input) => input.checked);

		if (globalThis.neuroCreate.saveGraph && analyseAnswers.length > 0) {
			globalThis.neuroCreate.saveGraph(
				'ideate',
				{
					analyses: analyseAnswers,
					links: [],
					nodes: [],
					positioningInsight: positioningInsight
						? getInsightName(positioningInsight)
						: undefined,
				},
				() => {
					dismissRecommender('started');
					(globalThis.neuroCreate.graph as IdeateGraph).finishedRecommenderForm();
				}
			);
		}
	});
}

insights.forEach((insight) => {
	insight.addEventListener('click', () => {
		const insightName = getInsightName(insight);
		if (insightName) renderRecommendationsForInsight(insightName);
	});
	if (insight.id === 'q1a1') insight.click();
});
